import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  QrCode, Send, Download, CheckCircle, XCircle,
  Loader2, Wifi, WifiOff, AlertTriangle, ArrowRight, Smartphone,
  Monitor, ClipboardList, FileDown, Copy,
} from 'lucide-react';
import {
  getAllCountPackages,
  exportCountPackage,
  importCountPackage,
  markReadyForTransfer,
  COUNT_SYNC_STATES,
  getDeviceId,
} from '../../services/countOfflineService';

// ── QR Transfer: encode/decode compressed package ──────
function compressForQR(data) {
  const json = JSON.stringify(data);
  return btoa(unescape(encodeURIComponent(json)));
}

function decompressFromQR(encoded) {
  const json = decodeURIComponent(escape(atob(encoded)));
  return JSON.parse(json);
}

// ── Main Transfer Page ──────────────────────────
export default function CountTransferPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState(null); // 'send' | 'receive'
  const [packages, setPackages] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [transferStep, setTransferStep] = useState('idle'); // idle, transferring, done, error
  const [qrData, setQrData] = useState(null);
  const [importText, setImportText] = useState('');
  const [statusMessage, setStatusMessage] = useState('');
  const fileInputRef = useRef(null);

  const deviceId = getDeviceId();
  const deviceType = navigator.userAgent.includes('Mobile') ? 'Tablet' : 'Desktop';

  // Load local packages ready for transfer
  useEffect(() => {
    loadPackages();
  }, []);

  const loadPackages = async () => {
    try {
      const allPkgs = await getAllCountPackages();
      const pkgList = Object.entries(allPkgs).map(([id, pkg]) => ({
        id: parseInt(id),
        ...pkg,
        linesCounted: Object.keys(pkg.local_counts || {}).filter(k => pkg.local_counts[k]?.quantity != null).length,
        totalLines: pkg.package?.count_sheet_lines?.length || 0,
      }));
      setPackages(pkgList);
    } catch (_) {
      setPackages([]);
    }
  };

  // Transferable packages: counting or ready_for_transfer state
  const transferablePackages = packages.filter(p =>
    [COUNT_SYNC_STATES.COUNTING, COUNT_SYNC_STATES.READY_FOR_TRANSFER].includes(p.sync_state)
  );

  // ── SEND: Mark ready and prepare export ──────
  const handlePrepareTransfer = useCallback(async (pkg) => {
    setSelectedSession(pkg);
    if (pkg.sync_state !== COUNT_SYNC_STATES.READY_FOR_TRANSFER) {
      await markReadyForTransfer(pkg.id, `${user?.first_name} ${user?.last_name}`);
    }
    setTransferStep('idle');
  }, [user]);

  // ── BLUETOOTH: Test connection ──────
  // ── FILE: Export as downloadable file ──────
  const handleQRExport = useCallback(async () => {
    if (!selectedSession) return;
    setTransferStep('transferring');
    setStatusMessage('Exporting count package...');
    try {
      const payload = await exportCountPackage(selectedSession.id);
      const jsonStr = JSON.stringify(payload, null, 0);

      // Create downloadable file
      const blob = new Blob([jsonStr], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `count-${payload.session_reference || selectedSession.id}-${Date.now()}.json`;
      a.click();
      URL.revokeObjectURL(url);

      // Also show QR for small packages
      if (jsonStr.length < 2000) {
        setQrData(compressForQR(payload));
      }

      setTransferStep('done');
      setStatusMessage('Package exported as file. Transfer this file to the receiving device.');
      toast.success(`Package exported for ${selectedSession.session_reference}`);
    } catch (err) {
      setTransferStep('error');
      setStatusMessage(`Export failed: ${err.message}`);
    }
  }, [selectedSession, toast]);

  // ── RECEIVE: Import from file ──────
  const handleFileImport = useCallback(async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setTransferStep('transferring');
    setStatusMessage('Reading transfer file...');
    try {
      const text = await file.text();
      const payload = JSON.parse(text);
      if (payload._type !== 'az_count_transfer') {
        throw new Error('Invalid transfer file — not an AbbeyZone Count package');
      }
      setStatusMessage(`Importing ${payload.session_reference} (${payload.lines_counted} lines)...`);
      const result = await importCountPackage(payload, `${user?.first_name} ${user?.last_name}`);
      setTransferStep('done');
      setStatusMessage(`Imported ${result.lines_imported} count entries for ${payload.session_reference}.`);
      toast.success(`${result.lines_imported} lines imported from ${payload.source_device_label}`);
      await loadPackages();
    } catch (err) {
      setTransferStep('error');
      setStatusMessage(`Import failed: ${err.message}`);
    }
  }, [user, toast]);

  // ── RECEIVE: Import from paste ──────
  const handlePasteImport = useCallback(async () => {
    if (!importText.trim()) return;
    setTransferStep('transferring');
    try {
      let payload;
      try {
        payload = JSON.parse(importText.trim());
      } catch (_) {
        payload = decompressFromQR(importText.trim());
      }
      if (payload._type !== 'az_count_transfer') throw new Error('Invalid data');
      const result = await importCountPackage(payload, `${user?.first_name} ${user?.last_name}`);
      setTransferStep('done');
      setStatusMessage(`Imported ${result.lines_imported} lines for ${payload.session_reference}.`);
      toast.success(`${result.lines_imported} lines imported`);
      setImportText('');
      await loadPackages();
    } catch (err) {
      setTransferStep('error');
      setStatusMessage(`Import failed: ${err.message}`);
    }
  }, [importText, user, toast]);

  return (
    <div className="p-4 max-w-2xl mx-auto" data-testid="count-transfer-page">
      <h1 className="text-xl font-bold text-slate-900 mb-1">Transfer Count Data</h1>
      <p className="text-sm text-slate-500 mb-4">Transfer completed counts between devices when there is no internet connection.</p>

      {/* Device Info */}
      <div className="flex items-center gap-2 mb-4 text-xs text-slate-500">
        {deviceType === 'Tablet' ? <Smartphone className="h-3.5 w-3.5" /> : <Monitor className="h-3.5 w-3.5" />}
        <span>This device: {deviceType} ({deviceId.slice(0, 12)}...)</span>
        <Badge variant="outline" className="text-[9px] bg-emerald-50 text-emerald-700">USB File Transfer</Badge>
      </div>

      {/* Mode Selection */}
      {!mode && (
        <div className="grid grid-cols-2 gap-3 mb-4">
          <Card className="cursor-pointer hover:border-purple-400 transition-colors" onClick={() => setMode('send')} data-testid="transfer-mode-send">
            <CardContent className="pt-6 text-center">
              <Send className="h-8 w-8 mx-auto text-purple-600 mb-2" />
              <p className="font-semibold text-sm">Send Counts</p>
              <p className="text-[11px] text-muted-foreground mt-1">Transfer completed counts from this tablet to another device</p>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:border-teal-400 transition-colors" onClick={() => setMode('receive')} data-testid="transfer-mode-receive">
            <CardContent className="pt-6 text-center">
              <Download className="h-8 w-8 mx-auto text-teal-600 mb-2" />
              <p className="font-semibold text-sm">Receive Counts</p>
              <p className="text-[11px] text-muted-foreground mt-1">Import count data from another device (tablet or file)</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* SEND MODE */}
      {mode === 'send' && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold">Send Count Package</h2>
            <Button size="sm" variant="ghost" className="text-xs" onClick={() => { setMode(null); setSelectedSession(null); setTransferStep('idle'); }}>Back</Button>
          </div>

          {!selectedSession ? (
            <>
              <p className="text-xs text-slate-500">Select a session to transfer:</p>
              {transferablePackages.length === 0 ? (
                <p className="text-sm text-slate-400 py-8 text-center">No sessions ready for transfer. Count some items first.</p>
              ) : (
                <div className="space-y-2">
                  {transferablePackages.map(pkg => (
                    <Card key={pkg.id} className="cursor-pointer hover:border-purple-300 transition-colors" onClick={() => handlePrepareTransfer(pkg)} data-testid={`transfer-session-${pkg.id}`}>
                      <CardContent className="py-3 flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium">{pkg.session_reference || `Session ${pkg.id}`}</p>
                          <p className="text-[11px] text-muted-foreground">{pkg.site_name} | {pkg.linesCounted}/{pkg.totalLines} counted</p>
                        </div>
                        <ArrowRight className="h-4 w-4 text-slate-400" />
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </>
          ) : (
            <div className="space-y-3">
              <Card className="border-purple-200 bg-purple-50/30">
                <CardContent className="py-3">
                  <div className="flex items-center gap-2">
                    <ClipboardList className="h-4 w-4 text-purple-600" />
                    <div>
                      <p className="text-sm font-medium">{selectedSession.session_reference}</p>
                      <p className="text-[11px] text-muted-foreground">{selectedSession.linesCounted} lines counted</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Transfer Method Selection */}
              {transferStep === 'idle' && (
                <div className="space-y-2">
                  <Button variant="outline" className="w-full h-auto py-3 justify-start gap-3" onClick={handleQRExport} data-testid="qr-export-btn">
                    <div className="bg-teal-100 p-1.5 rounded-lg shrink-0">
                      <FileDown className="h-4 w-4 text-teal-700" />
                    </div>
                    <div className="text-left">
                      <span className="text-sm font-medium block">Save as File</span>
                      <span className="text-[10px] text-muted-foreground">Export count data for USB transfer to computer</span>
                    </div>
                  </Button>
                </div>
              )}

              {/* Transfer Status */}
              {transferStep !== 'idle' && (
                <Card className={transferStep === 'done' ? 'border-emerald-300 bg-emerald-50/50' : transferStep === 'error' ? 'border-red-300 bg-red-50/50' : 'border-blue-300 bg-blue-50/50'}>
                  <CardContent className="py-3">
                    <div className="flex items-center gap-2 mb-1">
                      {transferStep === 'transferring' && <Loader2 className="h-4 w-4 animate-spin text-blue-600" />}
                      {transferStep === 'done' && <CheckCircle className="h-4 w-4 text-emerald-600" />}
                      {transferStep === 'error' && <XCircle className="h-4 w-4 text-red-600" />}
                      <p className="text-sm">{statusMessage}</p>
                    </div>
                    {transferStep === 'done' && (
                      <Button size="sm" variant="outline" className="mt-2" onClick={() => { setSelectedSession(null); setTransferStep('idle'); }}>Transfer Another</Button>
                    )}
                    {transferStep === 'error' && (
                      <Button size="sm" variant="outline" className="mt-2" onClick={() => setTransferStep('idle')}>Try Again</Button>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </div>
      )}

      {/* RECEIVE MODE */}
      {mode === 'receive' && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold">Receive Count Package</h2>
            <Button size="sm" variant="ghost" className="text-xs" onClick={() => { setMode(null); setTransferStep('idle'); }}>Back</Button>
          </div>

          <Card>
            <CardContent className="py-4 space-y-3">
              <div>
                <p className="text-sm font-medium mb-2">Import from File</p>
                <p className="text-xs text-muted-foreground mb-2">Select a .json count package file exported from another device.</p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".json"
                  onChange={handleFileImport}
                  className="hidden"
                />
                <Button variant="outline" size="sm" className="gap-1.5" onClick={() => fileInputRef.current?.click()} data-testid="import-file-btn">
                  <Download className="h-3.5 w-3.5" />Import File
                </Button>
              </div>

              <div className="border-t pt-3">
                <p className="text-sm font-medium mb-2">Import from Clipboard / Paste</p>
                <p className="text-xs text-muted-foreground mb-2">Paste the count package JSON data received via clipboard or file.</p>
                <textarea
                  className="w-full h-20 text-xs border rounded-lg p-2 font-mono resize-none"
                  placeholder='Paste JSON data here...'
                  value={importText}
                  onChange={e => setImportText(e.target.value)}
                  data-testid="import-paste-input"
                />
                <Button size="sm" className="mt-1 gap-1" disabled={!importText.trim()} onClick={handlePasteImport} data-testid="import-paste-btn">
                  <Download className="h-3 w-3" />Import
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Transfer Status */}
          {transferStep !== 'idle' && (
            <Card className={transferStep === 'done' ? 'border-emerald-300 bg-emerald-50/50' : transferStep === 'error' ? 'border-red-300 bg-red-50/50' : 'border-blue-300 bg-blue-50/50'}>
              <CardContent className="py-3 flex items-center gap-2">
                {transferStep === 'transferring' && <Loader2 className="h-4 w-4 animate-spin text-blue-600" />}
                {transferStep === 'done' && <CheckCircle className="h-4 w-4 text-emerald-600" />}
                {transferStep === 'error' && <XCircle className="h-4 w-4 text-red-600" />}
                <p className="text-sm">{statusMessage}</p>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}
